import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BsPlusLg, BsTrash, BsX } from "react-icons/bs";
import {
  FiUsers, FiSearch, FiCheck, FiStar, FiMail,
  FiShield, FiTrendingUp, FiChevronRight,
  FiUserPlus, FiUserMinus, FiList, FiEdit3,
} from "react-icons/fi";
import { HiOutlineSparkles } from "react-icons/hi2";
import {
  addToHotlist,
  createHotlistGroup,
  deleteHotlistGroup,
  getHostlistGroupCandidates,
  getHostlistGroupNames,
  removeFromHotlist,
  triggerEmail,
  updateHotListGroup,
} from "../service/hotlistApi";

const ALLOWED_EMAIL_DOMAINS = ["vdartinc.com", "vdartdigital.com"];

/* ─── normalizers ──────────────────────────────────────────────────────────── */
const toGroups = (payload) => {
  const rows = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.groups)
      ? payload.groups
      : Array.isArray(payload?.group_names)
        ? payload.group_names
        : Array.isArray(payload)
          ? payload
          : [];
  return rows
    .map((g, i) => {
      if (typeof g === "string")
        return { group_id: String(i + 1), name: g.trim(), count: 0 };
      const rawGroupId = g?.group_id ?? g?.hotlist_group_id ?? g?.id ?? g?.groupId ?? i + 1;
      return {
        group_id: String(rawGroupId ?? "").trim(),
        name: String(g?.name ?? g?.group_name ?? g?.hotlist_group_name ?? g?.group ?? "").trim(),
        count: Number(
          g?.count ??
          g?.candidate_count ??
          g?.candidates_count ??
          g?.candidateCount ??
          g?.hotlist_count ??
          g?.hotlist_candidates_count ??
          g?.total_candidates ??
          g?.no_of_candidates ??
          g?.total ??
          (Array.isArray(g?.members) ? g.members.length : 0)
        ) || 0,
      };
    })
    .filter((g) => g.group_id && g.name);
};

const toCandidates = (payload) => {
  const pick = (obj, keys) => {
    for (const key of keys) {
      const value = obj?.[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }
    return "";
  };
  const rows = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.candidates)
      ? payload.candidates
      : Array.isArray(payload?.data?.candidates)
        ? payload.data.candidates
        : Array.isArray(payload?.members)
          ? payload.members
          : Array.isArray(payload?.data?.members)
            ? payload.data.members
            : Array.isArray(payload?.groups)
              ? payload.groups.flatMap((g) => (Array.isArray(g?.members) ? g.members : []))
              : Array.isArray(payload)
                ? payload
                : [];
  return rows.map((c, i) => {
    const cid = Number(pick(c, ["candidate_id", "hgm.candidate_id", "id"]));
    const fallbackName = Number.isFinite(cid) ? `Candidate ${cid}` : "N/A";
    return {
      id: Number.isFinite(cid) ? cid : `row-${i}`,
      candidate_id: Number.isFinite(cid) ? cid : null,
      name: String(pick(c, ["name", "candidate_name", "rc.candidate_name"])).trim() || fallbackName,
      roles: String(pick(c, ["roles", "title", "job_title", "rc.job_title"])).trim() || "N/A",
      workedWithVdart: String(pick(c, ["worked_with_vdart", "workedWithVdart", "rc.worked_with_vdart"])).trim() || "N/A",
      previousClient: String(pick(c, ["previous_client", "end_client", "client", "rc.client"])).trim() || "N/A",
      phone: String(pick(c, ["candidate_phone", "phone", "rc.candidate_phone"])).trim() || "N/A",
      email: String(pick(c, ["candidate_email", "email", "rc.candidate_email"])).trim() || "N/A",
      city: String(pick(c, ["city", "project_city", "rc.project_city"])).trim() || "N/A",
      state: String(pick(c, ["state", "project_state", "rc.project_state"])).trim() || "N/A",
      country: String(pick(c, ["country", "project_country", "rc.project_country"])).trim() || "N/A",
      workAuthorization: String(
        pick(c, ["work_authorization_name", "work_authorization", "workAuthorization", "notes", "wam.authorization_name"])
      ).trim() || "N/A",
    };
  });
};

/* ─── avatar helper ──────────────────────────────────────────────────────── */
const AVATAR_COLORS = [
  ["#6366f1","#818cf8"], ["#0ea5e9","#38bdf8"], ["#10b981","#34d399"],
  ["#f59e0b","#fbbf24"], ["#ec4899","#f472b6"], ["#8b5cf6","#a78bfa"],
];
const Avatar = ({ name }) => {
  const idx = name.charCodeAt(0) % AVATAR_COLORS.length;
  const [from, to] = AVATAR_COLORS[idx];
  const initials = name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <div style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}
      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-xs font-bold text-white shadow-md">
      {initials}
    </div>
  );
};

/* ─── badge ──────────────────────────────────────────────────────────────── */
const Badge = ({ children, color = "slate" }) => {
  const colors = {
    slate: "bg-slate-100 text-slate-600",
    indigo: "bg-indigo-50 text-indigo-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${colors[color]}`}>
      {children}
    </span>
  );
};

/* ─── modal wrapper ──────────────────────────────────────────────────────── */
const Modal = ({ show, onClose, title, icon: Icon, children }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4" style={{ background: "rgba(2,6,23,0.55)", backdropFilter: "blur(6px)" }}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl" style={{ animation: "modalIn .18s cubic-bezier(.34,1.56,.64,1) both" }}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-2.5">
            {Icon && <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50"><Icon className="h-4 w-4 text-indigo-600" /></div>}
            <h4 className="text-sm font-bold text-slate-800">{title}</h4>
          </div>
          <button type="button" onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
            <BsX className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
};

/* ─── main component ─────────────────────────────────────────────────────── */
const HotlistHub = ({ initialHotlist = "", onHotlistSelect, actionBy = 1 }) => {
  const TABLE_GRID_TEMPLATE = "36px minmax(180px,1.8fr) minmax(150px,1.4fr) minmax(130px,1.1fr) minmax(150px,1.3fr) minmax(130px,1.1fr) minmax(220px,1.8fr) minmax(110px,1fr) minmax(110px,1fr) minmax(110px,1fr) minmax(140px,1.2fr)";
  const [hotlists, setHotlists] = useState([]);
  const [selectedHotlist, setSelectedHotlist] = useState(initialHotlist || "");
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [editName, setEditName] = useState("");
  const [editTarget, setEditTarget] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [senderEmail, setSenderEmail] = useState("");
  const [ccEmailAddress, setCcEmailAddress] = useState("");
  const [bccEmailAddress, setBccEmailAddress] = useState("");
  const [showCcBccFields, setShowCcBccFields] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isRemovingSelected, setIsRemovingSelected] = useState(false);
  const [existingChoice, setExistingChoice] = useState("");
  const [newHotlistName, setNewHotlistName] = useState("");

  const selectedGroup = useMemo(
    () => hotlists.find((g) => g.name === selectedHotlist) || null,
    [hotlists, selectedHotlist],
  );
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  const currentUserName = useMemo(() => {
    const full = `${currentUser?.first_name || ""} ${currentUser?.last_name || ""}`.trim();
    return full || currentUser?.name || currentUser?.user_name || "System User";
  }, [currentUser?.first_name, currentUser?.last_name, currentUser?.name, currentUser?.user_name]);
  const currentUserId = useMemo(
    () => currentUser?.id ?? currentUser?.user_id ?? currentUser?.userId ?? "",
    [currentUser?.id, currentUser?.user_id, currentUser?.userId],
  );

  const loadCandidates = useCallback(async (groupId) => {
    if (!groupId) return setCandidates([]);
    setLoading(true); setError("");
    try {
      const data = await getHostlistGroupCandidates(String(groupId));
      const nextCandidates = toCandidates(data);
      setCandidates(nextCandidates); setSelectedRows(new Set());
      setHotlists((prev) =>
        prev.map((g) =>
          String(g.group_id) === String(groupId)
            ? { ...g, count: nextCandidates.length }
            : g
        )
      );
    } catch (e) { setError(e?.message || "Failed to load candidates"); }
    finally { setLoading(false); }
  }, []);

  const loadGroups = useCallback(async (preferred = "") => {
    setLoading(true); setError("");
    try {
      const data = await getHostlistGroupNames();
      const next = toGroups(data);
      setHotlists(next);
      const nextName = preferred || selectedHotlist || initialHotlist || next[0]?.name || "";
      setSelectedHotlist(nextName);
      onHotlistSelect?.(nextName);
    } catch (e) { setError(e?.message || "Failed to load hotlists"); }
    finally { setLoading(false); }
  }, [initialHotlist, onHotlistSelect, selectedHotlist]);

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { if (!showAdd) return; loadGroups(selectedHotlist); }, [showAdd, loadGroups, selectedHotlist]);
  useEffect(() => { loadCandidates(selectedGroup?.group_id); }, [loadCandidates, selectedGroup?.group_id]);
  useEffect(() => {
    if (!successMessage) return undefined;
    const timer = setTimeout(() => setSuccessMessage(""), 4000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const filtered = useMemo(() => {
    if (!search.trim()) return candidates;
    const term = search.toLowerCase();
    return candidates.filter((r) => [r.name, r.title, r.email, r.phone, r.skills].join(" ").toLowerCase().includes(term));
  }, [candidates, search]);

  const selectedCandidates = filtered.filter((r) => selectedRows.has(r.candidate_id));
  const isAddDisabled = !selectedCandidates.length || (!existingChoice && !newHotlistName.trim());
  const splitEmailList = useCallback((value) => (
    String(value || "")
      .split(/[;,]/)
      .map((item) => item.trim())
      .filter(Boolean)
  ), []);
  const isValidEmailFormat = useCallback((value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim()), []);
  const isAllowedEmailDomain = useCallback(
    (value) => {
      const normalized = String(value || "").trim().toLowerCase();
      return ALLOWED_EMAIL_DOMAINS.some((domain) => normalized.endsWith(`@${domain}`));
    },
    [],
  );

  const toggle = (id) => setSelectedRows((p) => {
    const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  const createGroup = async () => {
    const name = newName.trim(); if (!name) return;
    try { await createHotlistGroup({ name, action_by: Number(actionBy) || 1 }); setShowCreate(false); setNewName(""); await loadGroups(name); }
    catch (e) { setError(e?.message || "Create failed"); }
  };

  const renameGroup = async () => {
    const target = hotlists.find((g) => g.name === editTarget);
    const name = editName.trim(); if (!target || !name) return;
    try { await updateHotListGroup({ group_id: target.group_id, name, description: "" }); setEditTarget(""); setEditName(""); await loadGroups(name); }
    catch (e) { setError(e?.message || "Rename failed"); }
  };

  const addSelected = async () => {
    const group_name = newHotlistName.trim() || existingChoice;
    try {
      await Promise.all(selectedCandidates.map((c) => addToHotlist({ candidate_id: c.candidate_id, name: group_name, group_name, description: "", notes: "", action_by: Number(actionBy) || 1 })));
      setShowAdd(false); setExistingChoice(""); setNewHotlistName(""); await loadGroups(selectedHotlist);
    } catch (e) { setError(e?.message || "Add failed"); }
  };

  const deleteGroup = async (group) => {
    if (!group?.group_id) return;
    try {
      const userId = Number(actionBy) || 1;
      await deleteHotlistGroup({ group_id: group.group_id, action_by: userId, user_id: userId });
      const fallbackName = selectedHotlist === group.name ? hotlists.find((g) => g.name !== group.name)?.name || "" : selectedHotlist;
      await loadGroups(fallbackName);
      if (!fallbackName) setCandidates([]);
    } catch (e) { setError(e?.message || "Delete failed"); }
  };

  const removeSelected = async () => {
    if (!selectedGroup?.group_id) return;
    try {
      setIsRemovingSelected(true);
      await Promise.all(selectedCandidates.map((c) => removeFromHotlist({ candidate_id: c.candidate_id, group: selectedGroup.group_id, action_by: Number(actionBy) || 1, hard_delete: 0 })));
      const data = await getHostlistGroupCandidates(selectedGroup.group_id);
      setCandidates(toCandidates(data)); setSelectedRows(new Set()); await loadGroups(selectedHotlist);
    } catch (e) { setError(e?.message || "Remove failed"); }
    finally { setIsRemovingSelected(false); setShowDeleteConfirm(false); }
  };

  const submitHotlistEmail = async () => {
    if (!selectedGroup?.group_id) {
      setError("Please select a hotlist group.");
      return;
    }
    const toList = splitEmailList(senderEmail);
    const ccList = splitEmailList(ccEmailAddress);
    const bccList = splitEmailList(bccEmailAddress);
    const email_id = toList.join(",");
    const email_cc_address = ccList.join(",");
    const email_bcc_address = bccList.join(",");
    if (toList.length === 0) {
      setError("Email To is required.");
      return;
    }
    const invalidToEmail = toList.find((email) => !isValidEmailFormat(email) || !isAllowedEmailDomain(email));
    if (invalidToEmail) {
      setError(`Email To must contain valid @${ALLOWED_EMAIL_DOMAINS.join(" or @")} addresses.`);
      return;
    }
    const invalidCcEmail = ccList.find((email) => !isValidEmailFormat(email) || !isAllowedEmailDomain(email));
    if (invalidCcEmail) {
      setError(`CC must contain valid @${ALLOWED_EMAIL_DOMAINS.join(" or @")} addresses.`);
      return;
    }
    const invalidBccEmail = bccList.find((email) => !isValidEmailFormat(email) || !isAllowedEmailDomain(email));
    if (invalidBccEmail) {
      setError(`BCC must contain valid @${ALLOWED_EMAIL_DOMAINS.join(" or @")} addresses.`);
      return;
    }
    const ids = selectedCandidates
      .map((c) => c.candidate_id)
      .filter((id) => Number.isFinite(id))
      .map((id) => Number(id));
    if (ids.length === 0) {
      setError("Select at least one candidate.");
      return;
    }
    try {
      setIsSendingEmail(true);
      setError("");
      setSuccessMessage("");
      await triggerEmail({
        email_id,
        email_cc_address,
        email_bcc_address,
        group_id: selectedGroup.group_id,
        candidate_id: ids,
        email_by: currentUserName,
        user_name: currentUserName,
        user_id: currentUserId || String(Number(actionBy) || ""),
        
      });
      setSuccessMessage("Email sent successfully.");
      setShowEmailModal(false);
    } catch (e) {
      setError(e?.message || "Failed to send hotlist email");
      setSuccessMessage("");
    } finally {
      setIsSendingEmail(false);
    }
  };

  /* ─── render ─────────────────────────────────────────────────────────── */
  return (
    <>
      <style>{`
        @keyframes modalIn { from { opacity:0; transform:scale(.94) translateY(8px) } to { opacity:1; transform:scale(1) translateY(0) } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
        .hl-row { animation: fadeUp .18s ease both; }
        .hl-row:nth-child(2){animation-delay:.03s} .hl-row:nth-child(3){animation-delay:.06s}
        .hl-row:nth-child(4){animation-delay:.09s} .hl-row:nth-child(5){animation-delay:.12s}
        .hl-row:nth-child(6){animation-delay:.15s}
        .group-item:hover .group-actions { opacity:1; }
        .group-actions { opacity:0; transition:opacity .15s; }
        .hl-checkbox:checked { accent-color: #4f46e5; }
      `}</style>

      <div className="min-h-screen p-6" style={{ background: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 50%, #f0fdf4 100%)" }}>
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl shadow-lg"
              style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
              <HiOutlineSparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.02em" }}>
                Hotlist Hub
              </h1>
              <p className="text-xs text-slate-400 font-medium">Manage your candidate hotlists</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-full border border-indigo-100 bg-white px-3 py-1.5 shadow-sm">
              <FiTrendingUp className="h-3.5 w-3.5 text-indigo-500" />
              <span className="text-xs font-semibold text-indigo-600">{hotlists.length} Lists</span>
            </div>
            <div className="flex items-center gap-1.5 rounded-full border border-emerald-100 bg-white px-3 py-1.5 shadow-sm">
              <FiUsers className="h-3.5 w-3.5 text-emerald-500" />
              <span className="text-xs font-semibold text-emerald-600">{candidates.length} Candidates</span>
            </div>
          </div>
        </div>

        {/* Success banner */}
        {successMessage && (
          <div className="mb-4 flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 shadow-sm">
            <FiCheck className="h-4 w-4 flex-shrink-0 text-emerald-600" />
            <span className="text-xs font-medium text-emerald-700">{successMessage}</span>
            <button onClick={() => setSuccessMessage("")} className="ml-auto text-emerald-400 hover:text-emerald-600"><BsX /></button>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="mb-4 flex items-center gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 shadow-sm">
            <FiShield className="h-4 w-4 flex-shrink-0 text-rose-500" />
            <span className="text-xs font-medium text-rose-700">{error}</span>
            <button onClick={() => setError("")} className="ml-auto text-rose-400 hover:text-rose-600"><BsX /></button>
          </div>
        )}

        {/* Toolbar */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 rounded-xl border border-indigo-200 bg-white px-4 py-2.5 text-xs font-semibold text-indigo-700 shadow-sm transition-all hover:bg-indigo-50 hover:shadow-md active:scale-95">
            <BsPlusLg className="h-3.5 w-3.5" /> New List
          </button>
          <button type="button" onClick={() => setShowAdd(true)} disabled={!selectedCandidates.length}
            className="flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-white px-4 py-2.5 text-xs font-semibold text-emerald-700 shadow-sm transition-all hover:bg-emerald-50 hover:shadow-md active:scale-95 disabled:cursor-not-allowed disabled:opacity-40">
            <FiUserPlus className="h-3.5 w-3.5" /> Add to List
            {selectedCandidates.length > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-bold text-white">
                {selectedCandidates.length}
              </span>
            )}
          </button>
          {selectedCandidates.length > 0 && selectedGroup && (
            <button type="button" onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-1.5 rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-xs font-semibold text-rose-600 shadow-sm transition-all hover:bg-rose-50 hover:shadow-md active:scale-95">
              <FiUserMinus className="h-3.5 w-3.5" /> Delete Candidate
            </button>
          )}
          {selectedCandidates.length > 0 && (
            <button type="button" onClick={() => { setSenderEmail(""); setCcEmailAddress(""); setBccEmailAddress(""); setShowCcBccFields(false); setSuccessMessage(""); setShowEmailModal(true); }}
              className="flex items-center gap-1.5 rounded-xl border border-sky-200 bg-white px-4 py-2.5 text-xs font-semibold text-sky-700 shadow-sm transition-all hover:bg-sky-50 hover:shadow-md active:scale-95">
              <FiMail className="h-3.5 w-3.5" /> Email
            </button>
          )}
          <div className="relative ml-auto">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search candidates..."
              className="w-64 rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-xs shadow-sm outline-none transition-all placeholder:text-slate-400 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100" />
          </div>
        </div>

        {/* Main grid */}
        <div className="grid gap-4 lg:grid-cols-[260px_1fr]">

          {/* Sidebar */}
          <div className="flex flex-col gap-3">
            {/* Group count pill */}
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 shadow-sm">
              <FiList className="h-3.5 w-3.5 text-indigo-400" />
              <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Hotlists</span>
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-indigo-100 px-1.5 text-[10px] font-bold text-indigo-700">
                {hotlists.length}
              </span>
            </div>

            {/* Group list */}
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              {hotlists.length === 0 && !loading && (
                <div className="flex flex-col items-center gap-2 py-10 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100"><FiStar className="h-5 w-5 text-slate-300" /></div>
                  <span className="text-xs text-slate-400">No hotlists yet</span>
                  <button onClick={() => setShowCreate(true)} className="text-xs font-semibold text-indigo-600 hover:underline">Create one →</button>
                </div>
              )}
              {hotlists.map((list, idx) => {
                const isActive = selectedHotlist === list.name;
                const isEditing = editTarget === list.name;
                return (
                  <div key={list.group_id}
                    className={`group-item flex items-center gap-2 border-b border-slate-100 px-3 py-3 last:border-0 transition-colors ${isActive ? "bg-gradient-to-r from-indigo-50 to-violet-50" : "hover:bg-slate-50/70"}`}>
                    <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl text-xs font-bold transition-all ${isActive ? "bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-200" : "bg-slate-100 text-slate-500"}`}>
                      {list.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      {isEditing ? (
                        <input value={editName} onChange={(e) => setEditName(e.target.value)}
                          className="w-full rounded border border-indigo-200 bg-white px-2 py-1 text-xs text-slate-700 outline-none focus:ring-2 focus:ring-indigo-100"
                          onKeyDown={(e) => e.key === "Enter" && renameGroup()} autoFocus />
                      ) : (
                        <button type="button" onClick={() => { setSelectedHotlist(list.name); onHotlistSelect?.(list.name); loadCandidates(list.group_id); }}
                          className={`block w-full truncate text-left text-sm font-semibold ${isActive ? "text-indigo-800" : "text-slate-700"}`}>
                          {list.name}
                        </button>
                      )}
                      <div className="mt-0.5 flex items-center gap-1">
                        {/* <FiUsers className="h-2.5 w-2.5 text-slate-400" />
                        <span className="text-[10px] text-slate-400">
                          {isActive ? candidates.length : list.count} candidates
                        </span> */}
                      </div>
                    </div>
                    <div className="group-actions flex items-center gap-1">
                      {isEditing ? (
                        <button type="button" onClick={renameGroup}
                          className="flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-500 text-white hover:bg-emerald-600">
                          <FiCheck className="h-3 w-3" />
                        </button>
                      ) : (
                        <button type="button" onClick={() => { setEditTarget(list.name); setEditName(list.name); }}
                          className="flex h-6 w-6 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200 hover:text-slate-700">
                          <FiEdit3 className="h-3 w-3" />
                        </button>
                      )}
                      <button type="button" onClick={() => deleteGroup(list)}
                        className="flex h-6 w-6 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-100 hover:text-rose-600">
                        <BsTrash className="h-3 w-3" />
                      </button>
                    </div>
                    {isActive && <FiChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-indigo-400" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Candidate table */}
          <div className="overflow-x-auto overflow-y-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            {/* Table header bar */}
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-5 py-3">
              <div className="flex items-center gap-2">
                {selectedGroup ? (
                  <>
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg"
                      style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
                      <FiUsers className="h-3.5 w-3.5 text-white" />
                    </div>
                    <span className="text-sm font-bold text-slate-800">{selectedGroup.name}</span>
                    <Badge color="indigo">{filtered.length} shown</Badge>
                    {selectedRows.size > 0 && <Badge color="emerald">{selectedRows.size} selected</Badge>}
                  </>
                ) : (
                  <span className="text-sm text-slate-400">Select a hotlist</span>
                )}
              </div>
              {selectedCandidates.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-slate-500">{selectedCandidates.length} selected</span>
                  <button type="button" onClick={() => setSelectedRows(new Set())}
                    className="rounded-lg px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-200">
                    Clear
                  </button>
                </div>
              )}
            </div>

            {/* Column headers */}
            <div className="grid min-w-[1550px] items-center gap-3 border-b border-slate-100 px-5 py-3"
              style={{ gridTemplateColumns: TABLE_GRID_TEMPLATE }}>
              <input type="checkbox" className="hl-checkbox h-3.5 w-3.5 rounded border-slate-300 cursor-pointer"
                checked={filtered.length > 0 && filtered.every((r) => selectedRows.has(r.candidate_id))}
                onChange={() => {
                  if (filtered.every((r) => selectedRows.has(r.candidate_id)))
                    setSelectedRows(new Set());
                  else setSelectedRows(new Set(filtered.filter(r => Number.isFinite(r.candidate_id)).map((r) => r.candidate_id)));
                }} />
              {["Candidate Name", "Roles", "Worked with VDart", "Previous client", "Contact No", "Email ID", "City", "State", "Country", "Work Authorization"].map((h) => (
                <span key={h} className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{h}</span>
              ))}
            </div>

            {/* Rows */}
            <div className="divide-y divide-slate-100">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-16">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
                  <span className="text-xs text-slate-400">Loading candidates…</span>
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-16 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100">
                    <FiUsers className="h-6 w-6 text-slate-300" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-500">No candidates found</p>
                    <p className="text-xs text-slate-400 mt-0.5">{search ? "Try a different search" : "Select a hotlist or add candidates"}</p>
                  </div>
                </div>
              ) : (
                filtered.map((row) => {
                  const isSelected = selectedRows.has(row.candidate_id);
                  return (
                    <div key={row.id}
                      className={`hl-row grid min-w-[1550px] items-center gap-3 px-5 py-3.5 transition-colors ${isSelected ? "bg-indigo-50/60" : "hover:bg-slate-50/70"}`}
                      style={{ gridTemplateColumns: TABLE_GRID_TEMPLATE }}>
                      <input type="checkbox" className="hl-checkbox h-3.5 w-3.5 rounded border-slate-300 cursor-pointer"
                        checked={isSelected} onChange={() => toggle(row.candidate_id)}
                        disabled={!Number.isFinite(row.candidate_id)} />
                      <div className="flex min-w-0 items-center gap-2.5">
                        <Avatar name={row.name} />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-slate-900">{row.name}</div>
                        </div>
                      </div>
                      <span className="truncate text-xs text-slate-600">{row.roles}</span>
                      <span className="truncate text-xs text-slate-600">{row.workedWithVdart}</span>
                      <span className="truncate text-xs text-slate-600">{row.previousClient}</span>
                      <span className="truncate text-xs text-slate-600">{row.phone}</span>
                      <a href={`mailto:${row.email}`} className="truncate text-xs text-indigo-600 hover:underline">{row.email}</a>
                      <span className="truncate text-xs text-slate-600">{row.city}</span>
                      <span className="truncate text-xs text-slate-600">{row.state}</span>
                      <span className="truncate text-xs text-slate-600">{row.country}</span>
                      <span className={`truncate text-[11px] font-medium ${row.workAuthorization !== "N/A" ? "text-emerald-700" : "text-slate-400"}`}>
                        {row.workAuthorization}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Create modal ─────────────────────────────────────────────── */}
      <Modal show={showCreate} onClose={() => { setShowCreate(false); setNewName(""); }} title="Create New Hotlist" icon={BsPlusLg}>
        <p className="mb-3 text-xs text-slate-500">Give your hotlist a descriptive name to help organize your candidates.</p>
        <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Senior React Developers"
          className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
          onKeyDown={(e) => e.key === "Enter" && createGroup()} autoFocus />
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={() => { setShowCreate(false); setNewName(""); }}
            className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button type="button" onClick={createGroup} disabled={!newName.trim()}
            className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold text-white shadow-sm shadow-indigo-200 transition disabled:opacity-40"
            style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}>
            <BsPlusLg /> Create List
          </button>
        </div>
      </Modal>

      {/* ── Add to hotlist modal ─────────────────────────────────────── */}
      <Modal show={showAdd} onClose={() => { setShowAdd(false); setExistingChoice(""); setNewHotlistName(""); }} title="Add to Hotlist" icon={FiUserPlus}>
        <p className="mb-3 text-xs text-slate-500">Adding <strong className="text-slate-700">{selectedCandidates.length}</strong> candidate{selectedCandidates.length !== 1 ? "s" : ""} to a hotlist.</p>
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Select existing list</label>
        <select value={existingChoice} onChange={(e) => setExistingChoice(e.target.value)}
          className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100">
          <option value="">Choose a list…</option>
          {hotlists.map((h) => <option key={h.group_id} value={h.name}>{h.name}</option>)}
        </select>
        <div className="my-3 flex items-center gap-2">
          <div className="h-px flex-1 bg-slate-200" /><span className="text-[11px] font-medium text-slate-400">or create new</span><div className="h-px flex-1 bg-slate-200" />
        </div>
        <input value={newHotlistName} onChange={(e) => setNewHotlistName(e.target.value)} placeholder="New list name…"
          className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100" />
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={() => { setShowAdd(false); setExistingChoice(""); setNewHotlistName(""); }}
            className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button type="button" disabled={isAddDisabled} onClick={addSelected}
            className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold text-white shadow-sm shadow-emerald-200 transition disabled:opacity-40"
            style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
            <FiCheck /> Add Candidates
          </button>
        </div>
      </Modal>

      {/* ── Email modal ─────────────────────────────────────────────── */}
      <Modal show={showEmailModal} onClose={() => { setShowCcBccFields(false); setShowEmailModal(false); }} title="Email Candidate Profiles" icon={FiMail}>
        <p className="mb-3 text-xs text-slate-500">
          Send email for <strong className="text-slate-700">{selectedCandidates.length}</strong> selected candidate{selectedCandidates.length !== 1 ? "s" : ""}.
        </p>
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Email To</label>
        <input
          value={senderEmail}
          onChange={(e) => setSenderEmail(e.target.value)}
          placeholder="name@vdartinc.com, name@vdartdigital.com"
          className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
        />
        <button
          type="button"
          onClick={() => setShowCcBccFields((prev) => !prev)}
          className="mt-3 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
        >
          {showCcBccFields ? "Hide CC/BCC" : "Add CC/BCC"}
        </button>
        {showCcBccFields && (
          <>
            <label className="mb-1 mt-3 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">CC</label>
            <input
              value={ccEmailAddress}
              onChange={(e) => setCcEmailAddress(e.target.value)}
              placeholder="cc1@vdartinc.com, cc2@vdartdigital.com"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
            />
            <label className="mb-1 mt-3 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">BCC</label>
            <input
              value={bccEmailAddress}
              onChange={(e) => setBccEmailAddress(e.target.value)}
              placeholder="bcc1@vdartinc.com, bcc2@vdartdigital.com"
              className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
            />
          </>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => { setShowCcBccFields(false); setShowEmailModal(false); }}
            className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submitHotlistEmail}
            disabled={isSendingEmail}
            className="flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-semibold text-white shadow-sm shadow-sky-200 transition disabled:opacity-50"
            style={{ background: "linear-gradient(135deg, #0ea5e9, #2563eb)" }}
          >
            <FiMail className="h-3.5 w-3.5" /> {isSendingEmail ? "Sending..." : "Send Email"}
          </button>
        </div>
      </Modal>

      <Modal
        show={showDeleteConfirm}
        onClose={() => { if (!isRemovingSelected) setShowDeleteConfirm(false); }}
        title="Delete Candidate"
        icon={FiUserMinus}
      >
        <p className="mb-3 text-xs text-slate-500">
          Delete <strong className="text-slate-700">{selectedCandidates.length}</strong> selected candidate{selectedCandidates.length !== 1 ? "s" : ""} from this hotlist?
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(false)}
            disabled={isRemovingSelected}
            className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={removeSelected}
            disabled={isRemovingSelected}
            className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-100 disabled:opacity-40"
          >
            {isRemovingSelected ? "Deleting..." : "OK"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default HotlistHub;
